// Reset & Cache utilities

const KEYS_TO_PRESERVE = [
  'admin_master_hash',
  'admin_master_salt',
  'admin_confirm_hash',
  'admin_confirm_salt',
  'admin_lockout_until',
  'admin_failed_attempts',
  'odoo_config_encrypted',
  'app_theme',
  'app_language',
];

const CACHE_KEYS_TO_CLEAR = [
  // Cached data
  'customers_cache', 'products_cache', 'orders_cache', 'collections_cache',
  'visits_cache', 'pricelists_cache', 'categories_cache',
  
  // Local records
  'local_customers', 'local_products', 'local_orders', 'local_visits', 'local_visits_history',
  
  // Active states
  'active_visit', 'current_user', 'auth_token', 'session_data', 'user_preferences',
  
  // Sync queue
  'pending_orders', 'pending_payments', 'pending_visits', 'sync_queue', 'last_sync_timestamp',
  
  // Cached images
  'product_images_cache', 'customer_images_cache',
  
  // Settings cache
  'app_settings_cache', 'kpi_cache', 'reports_cache', 'initial_sync_complete', 'last_full_sync',
];

export async function clearAllCache() {
  for (const key of CACHE_KEYS_TO_CLEAR) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.log(`Failed to delete ${key}:`, e);
    }
  }
  
  // Clear browser caches if available
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    } catch (e) {
      console.log('Failed to clear browser caches:', e);
    }
  }
}

export function determineResetLevel(oldConfig, newConfig) {
  if (!oldConfig) return 'FULL';
  
  if (oldConfig.serverUrl !== newConfig.serverUrl) return 'FULL';
  if (oldConfig.database !== newConfig.database) return 'FULL';
  if (oldConfig.username !== newConfig.username) return 'SOFT';
  if (oldConfig.apiKey !== newConfig.apiKey) return 'CACHE';
  
  return 'NONE';
}

export function getResetDescription(level) {
  const descriptions = {
    FULL: 'سيتم مسح جميع البيانات المحلية وتسجيل خروج جميع المستخدمين. ستحتاج إعادة مزامنة كاملة من الخادم الجديد.',
    SOFT: 'سيتم مسح البيانات المحلية وسيتم تسجيل الخروج. إعادة المزامنة ستتم بعد تسجيل الدخول مجددًا.',
    CACHE: 'سيتم مسح الذاكرة المؤقتة فقط وإعادة تحميل البيانات من الخادم.',
    NONE: 'لم يتم اكتشاف أي تغييرات مهمة.',
  };
  return descriptions[level] || '';
}

export async function logConfigChange(oldConfig, newConfig, resetLevel) {
  const entry = {
    timestamp: new Date().toISOString(),
    changes: {
      serverUrl: oldConfig?.serverUrl !== newConfig.serverUrl,
      database: oldConfig?.database !== newConfig.database,
      username: oldConfig?.username !== newConfig.username,
      apiKey: oldConfig?.apiKey !== newConfig.apiKey,
    },
    resetLevel,
  };
  
  const log = JSON.parse(localStorage.getItem('admin_audit_log') || '[]');
  log.push(entry);
  localStorage.setItem('admin_audit_log', JSON.stringify(log.slice(-50))); // Keep last 50
}

export async function performFullReset(newConfig, masterPassword) {
  return new Promise((resolve, reject) => {
    const steps = [
      { step: 1, text: 'حفظ الإعدادات الجديدة...' },
      { step: 2, text: 'مسح ذاكرة التخزين المؤقت...' },
      { step: 3, text: 'إلغاء المزامنة المعلقة...' },
      { step: 4, text: 'مسح البيانات المحلية...' },
      { step: 5, text: 'تسجيل خروج المستخدمين...' },
      { step: 6, text: 'إعادة تشغيل التطبيق...' },
    ];
    
    let currentStep = 0;
    const onProgress = window.__resetProgress || (() => {});
    
    const executeStep = async () => {
      if (currentStep >= steps.length) {
        onProgress(steps[steps.length - 1].step, '6', steps[steps.length - 1].text);
        setTimeout(() => {
          window.location.href = '/?reset=true&config_changed=true';
        }, 1500);
        resolve();
        return;
      }
      
      const current = steps[currentStep];
      onProgress(current.step, '6', current.text);
      
      try {
        switch (currentStep) {
          case 0: // Save new config
            const encrypted = window.__encryptConfig(newConfig, masterPassword);
            localStorage.setItem('odoo_config_encrypted', JSON.stringify(encrypted));
            break;
          case 1: // Clear cache
            await clearAllCache();
            break;
          case 2: // Cancel pending sync
            localStorage.removeItem('pending_orders');
            localStorage.removeItem('pending_payments');
            localStorage.removeItem('pending_visits');
            localStorage.removeItem('sync_queue');
            break;
          case 3: // Clear local data (already cleared in step 1, but ensure)
            for (const key of CACHE_KEYS_TO_CLEAR) {
              localStorage.removeItem(key);
            }
            break;
          case 4: // Force logout
            localStorage.removeItem('auth_token');
            localStorage.removeItem('session_data');
            localStorage.removeItem('current_user');
            break;
          case 5: // Prepare for redirect
            // Already prepared above
            break;
        }
      } catch (e) {
        console.error(`Step ${current.step} failed:`, e);
      }
      
      currentStep++;
      setTimeout(executeStep, 300);
    };
    
    executeStep();
  });
}